import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import Button from "app/storybookComponents/Button";
import NavigateBackButton from "app/components/NavigateBackButton";
import { Card, Dropdown } from "react-bootstrap";
import { getS } from "utils/helperFunctions";
import TableSearchInput from "app/components/TableSearchInput";
import TableEmptyState from "app/components/TableEmptyState";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getTEAMscanActivityTableData,
  selectAllTEAMscanActivityTableData,
  selectDepartments,
  selectGetTEAMscanActivityTableDataStatus,
} from "../slice";
import SortableTable from "app/components/SortableTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import { updateTeam360DateRange as updateTeamsTeam360DateRange } from "app/containers/TeamGuide/slice";
import { updateTeam360DateRange as updateDepartmentsTeam360DateRange } from "app/containers/DepartmentInsightReport/slice";
import {
  getAllTeamsByCompanyAccountId,
  selectAllCompanyUsersById,
  selectCompanyInfo,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { trackCSVExport } from "utils/trackingFunctions";
import { RowContent, TableHeader } from "app/components/SortableTable/types";
import {
  GetTEAMscanActivityTablePayload,
  SelectedTEAMscanBreakdownEntity,
  TEAMscanActivityTableRow,
} from "../types";
import TableDropdownMenu from "app/components/Dropdowns/TableDropdownMenu";
import RecordDropdown from "app/components/Dropdowns/RecordDropdown";
import {
  TEAMSCAN_ACTIVITY_CSV_TABLE_HEADERS,
  TEAMscanInitialTimeInterval,
  TEAMscanTimeIntervals,
} from "../constants";
import { useNavigate } from "react-router-dom";
import {
  getStartAndEndDateForInstanceId,
  getTEAMscanTableDataFromSelectedRow,
  getNameCSVCellValue,
  getCompanyName,
} from "../helpers";
import { selectChildCompanies } from "app/containers/Hierarchy/slice";
import { percentageColor } from "app/containers/Dashboard/helpers";
import {
  getDaysRemaining,
  getScoreClassName,
} from "app/containers/Assessment/helpers";
import {
  getAssessmentInstances,
  selectAllTeamAssessmentsInstances,
} from "app/containers/Assessment/slice";
import {
  ALL_TEAM_360_FACTORS,
  CONSTRUCT_NAME_KEY,
} from "app/components/Team360Assessment/constants";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import Toggle from "app/components/Toggle";

interface Props {
  companyAccountId?: string;
  companySelector?: ReactNode;
}

const TEAMscanActivityTable = ({ companyAccountId, companySelector }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // ---------------------------- Selectors ----------------------------
  const departments = useAppSelector(selectDepartments);
  const allTeamsById = useAppSelector(selectTeamsByTeamId);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const companyInfo = useAppSelector(selectCompanyInfo);
  const childCompanies = useAppSelector(selectChildCompanies);
  const allTEAMscanActivityTableData = useAppSelector(
    selectAllTEAMscanActivityTableData
  );
  const getTEAMscanActivityTableDataStatus = useAppSelector(
    selectGetTEAMscanActivityTableDataStatus
  );
  const allTeamAssessmentInstances = useAppSelector(
    selectAllTeamAssessmentsInstances
  );

  // ---------------------------- States ----------------------------
  const [displayedInput, setDisplayedInput] = useState<string>("");
  const [entitySelected, setEntitySelected] = useState<"team" | "department">(
    "team"
  );
  const [timeInterval, setTimeInterval] = useState(TEAMscanInitialTimeInterval);
  const [showAllDimensions, setShowAllDimensions] = useState(false);
  const [breakdownRowSelectedArray, setBreakdownRowSelectedArray] = useState<
    SelectedTEAMscanBreakdownEntity[] // Even though an array is not needed, will keep incase we want to nest one level deeper
  >([]);

  const breakdownRowSelected = useMemo(
    () => breakdownRowSelectedArray[0] ?? null,
    [breakdownRowSelectedArray]
  ) as SelectedTEAMscanBreakdownEntity | null;

  // ---------------------------- Effect ----------------------------

  // Needed to resolve the date range when viewing the results for a row (no-op if already fetched or in flight)
  useEffect(() => {
    dispatch(getAssessmentInstances());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAssessmentInstances());
    if (companyInfo?.companyAccountId) {
      dispatch(getAllTeamsByCompanyAccountId({ companyAccountId: companyInfo.companyAccountId }));
    }
  }, [dispatch, companyInfo?.companyAccountId]);

  useEffect(() => {
    dispatch(
      getTEAMscanActivityTableData({
        timeFrame: timeInterval as GetTEAMscanActivityTablePayload["timeFrame"],
        companyAccountId,
      })
    );
  }, [dispatch, timeInterval, entitySelected, companyAccountId]);

  // ---------------------------- Handler Functions ----------------------------

  const onSetDepartmentBreakdown = (row: TEAMscanActivityTableRow) => {
    const {
      id: departmentId,
      startDate,
      endDate = "",
      teamsInAssessmentInstance,
    } = row;
    setEntitySelected("team");
    const teamIds =
      teamsInAssessmentInstance ?? departments[departmentId]?.teams ?? [];

    setBreakdownRowSelectedArray([
      {
        type: "department",
        departmentId,
        teamIds,
        startDate,
        endDate,
      },
      ...breakdownRowSelectedArray,
    ]);
  };

  const onSeeTeamScanResultsForRow = (
    { id, assessmentInstanceId }: TEAMscanActivityTableRow,
    isDepartmentRow?: boolean
  ) => {
    const startAndEndDate = getStartAndEndDateForInstanceId(
      assessmentInstanceId,
      allTeamAssessmentInstances
    );

    if (!startAndEndDate) {
      return;
    }

    if (isDepartmentRow) {
      dispatch(
        updateDepartmentsTeam360DateRange({
          departmentId: id,
          startDate: startAndEndDate.startDate,
          endDate: startAndEndDate.endDate,
          instance: startAndEndDate.startDate,
        })
      );
    } else {
      dispatch(
        updateTeamsTeam360DateRange({
          teamId: id,
          startDate: startAndEndDate.startDate,
          endDate: startAndEndDate.endDate,
          instance: startAndEndDate.startDate,
        })
      );
    }
    navigate(
      `/${isDepartmentRow ? "Department" : "Team"}Guide/${id}?tab=TEAMscan`
    );
  };

  const onRowDropdownSelect = (
    e: string | null,
    row: TEAMscanActivityTableRow
  ) => {
    if (e === "teamScan") {
      onSeeTeamScanResultsForRow(row, entitySelected === "department");
    } else if (e === "breakdown" && entitySelected === "department") {
      onSetDepartmentBreakdown(row);
    }
  };

  const isTableLoading = getTEAMscanActivityTableDataStatus === "loading";

  // ---------------------------- Getter Functions ----------------------------
  const activityTableData = useMemo(() => {
    if (breakdownRowSelected) {
      return getTEAMscanTableDataFromSelectedRow(
        allTEAMscanActivityTableData,
        breakdownRowSelected,
        timeInterval,
        entitySelected
      );
    }

    const entitySelectedText = `${entitySelected}s`;
    return Object.values(
      allTEAMscanActivityTableData?.[timeInterval]?.[entitySelectedText] ?? {}
    );
  }, [
    breakdownRowSelected,
    allTEAMscanActivityTableData,
    entitySelected,
    timeInterval,
  ]);

  const getFilteredRows = useCallback((): TEAMscanActivityTableRow[] => {
    const rows = [...activityTableData];
    // If there is no input, return all rows
    if (!displayedInput) {
      return activityTableData;
    }

    if (entitySelected === "team") {
      return rows.filter((row) => {
        const team = allTeamsById[row.id];
        if (!team) {
          return false;
        }
        return team.teamName
          .toLowerCase()
          .includes(displayedInput.toLowerCase());
      });
    }

    if (entitySelected === "department") {
      return rows.filter((row) => {
        const department = departments[row.id];
        if (!department) {
          return false;
        }
        return department.name
          ?.toLowerCase()
          .includes(displayedInput.toLowerCase());
      });
    }

    // Otherwise, filter the rows by user name or email
    return rows.filter((row) => {
      const user = usersById[row.id];
      if (!user) {
        return false;
      }
      const { firstName = "", lastName = "", emailAddress } = user;
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        fullName.toLowerCase().includes(displayedInput.toLowerCase()) ||
        emailAddress.toLowerCase().includes(displayedInput.toLowerCase())
      );
    });
  }, [
    displayedInput,
    entitySelected,
    activityTableData,
    allTeamsById,
    departments,
    usersById,
  ]);

  const getSearchPlaceholder = () => {
    if (breakdownRowSelected) {
      return "Search for a user...";
    }
    switch (entitySelected) {
      case "team":
        return "Search for a team...";
      case "department":
        return "Search for a department...";
    }
  };

  const getCSVExportRows = useCallback(() => {
    trackCSVExport("TEAMscan Activity");
    return getFilteredRows().map((data) => {
      const completedAssessmentEventsCount =
        data.completedAssessmentEventsCount ?? 0;
      const assessmentEventsCount = data.assessmentEventsCount ?? 0;
      const completionRatePercentage = Math.round(
        (completedAssessmentEventsCount / assessmentEventsCount) * 100
      );
      const daysRemaining = getDaysRemaining(data.endDate);
      const emptyScoreString =
        daysRemaining && daysRemaining > 0 ? "Results Pending" : "No Results";
      const baseRow = {
        [entitySelected]: getNameCSVCellValue(data.id, entitySelected, {
          departments,
          teamsById: allTeamsById,
          usersById,
        }),
        overallScore: data.overallScore ?? emptyScoreString,
        completedCount: data.completedAssessmentEventsCount ?? 0,
        invitedCount: data.assessmentEventsCount ?? 0,
        peopleOnAverageAndBelow: data.peopleOnAverageOrBelowTeams ?? "0",
        peopleNotLikelyToStayOnTeam: data.peopleNotLikelyToStayOnTeam ?? "0",
        completionRate: `${completionRatePercentage}% (${completedAssessmentEventsCount}/${assessmentEventsCount})`,
        startDate: new Date(data.startDate).toLocaleDateString("en-US"),
      };

      ALL_TEAM_360_FACTORS.forEach((factor) => {
        baseRow[factor] = data.factorScores?.[factor] || "N/A";
      });

      Object.keys(CONSTRUCT_NAME_KEY).forEach((keys) => {
        baseRow[keys] = data.dimensionScores?.[keys] || "N/A";
      });

      return baseRow;
    });
  }, [getFilteredRows, entitySelected, departments, allTeamsById, usersById]);

  const onBackNavigate = () => {
    const newBreakdownRowSelectedArray = [...breakdownRowSelectedArray];
    const removedSelectedRow = newBreakdownRowSelectedArray.shift();

    if (removedSelectedRow?.type) {
      setEntitySelected(removedSelectedRow.type);
    }
    setBreakdownRowSelectedArray(newBreakdownRowSelectedArray);
  };

  const getDropdownSection = () => {
    if (breakdownRowSelected) {
      return null;
    }
    return (
      <div className="d-flex justify-content-between align-items-center flex-grow-1 md:flex-row  md:justify-content-start">
        <div className="row-gap-12px align-items-center">
          <RecordDropdown
            record={TEAMscanTimeIntervals}
            selected={timeInterval}
            onSelect={(e) => {
              setTimeInterval(e ?? TEAMscanInitialTimeInterval);
              setDisplayedInput("");
            }}
            isDisabled={isTableLoading}
          />
          <RecordDropdown
            record={{
              team: "Team",
              department: "Department",
            }}
            selected={entitySelected}
            onSelect={(e) => {
              setEntitySelected((e ?? "team") as "team" | "department");
              setDisplayedInput("");
            }}
            isDisabled={isTableLoading}
          />
          <div className="row-gap-8px align-items-center">
            <p>Show all dimensions</p>
            <Toggle
              isOn={showAllDimensions}
              handleToggle={() => setShowAllDimensions((prev) => !prev)}
              size="sm"
              disabled={isTableLoading}
            />
          </div>
        </div>
        <div>
          <ExportCSVButton
            headers={getCSVHeaders()}
            getRows={getCSVExportRows}
            fileName="TEAMscan Activity"
            disabled={isTableLoading}
          />
        </div>
      </div>
    );
  };

  const getScoreCell = (
    totalCount: number = 0,
    denominator: number = 0,
    options?: {
      showFraction?: boolean;
      inverseColor?: boolean;
      hasResults?: boolean;
    }
  ) => {
    const {
      showFraction = false,
      inverseColor = false,
      hasResults = false,
    } = options || {};
    const percentage = Math.round((totalCount / denominator) * 100);
    const dotColor = percentageColor(percentage, inverseColor);

    const displayText = showFraction
      ? `${percentage}% (${totalCount}/${denominator})`
      : `${totalCount} (${percentage}%)`;

    const displayValue = hasResults ? (
      <div className="row-gap-8px align-items-center">
        {displayText}
        <div className={`status-dot ${dotColor}`} />
      </div>
    ) : (
      <div className="d-flex">
        <p>-</p>
      </div>
    );

    return {
      sortValue: percentage,
      displayValue,
    };
  };

  const getOverallScore = (score: number = 0, endDate: string = "") => {
    const daysRemaining = getDaysRemaining(endDate);
    const emptyScoreString =
      daysRemaining && daysRemaining > 0 ? "Results Pending" : "No Results";
    return {
      displayValue: (
        <div className="d-flex justify-content-center">
          <div
            className={`verbal-tag bigger ${getScoreClassName(score)}`}
            style={{ width: "fit-content" }}
          >
            {score || emptyScoreString}
          </div>
        </div>
      ),
      sortValue: score,
    };
  };

  const getFactorOrDimensionScoreCell = (
    score: number | null | undefined,
    endDate: string = ""
  ) => {
    const daysRemaining = getDaysRemaining(endDate);
    const emptyScoreString =
      daysRemaining && daysRemaining > 0 ? "Results Pending" : "-";
    const numericScore = score ?? 0;
    return {
      displayValue: (
        <div className="d-flex justify-content-center">
          <div
            className={`verbal-tag bigger ${getScoreClassName(numericScore)}`}
            style={{ width: "fit-content" }}
          >
            {numericScore || emptyScoreString}
          </div>
        </div>
      ),
      sortValue: numericScore,
    };
  };

  const getEntityDisplayValue = (
    title: {
      label: string;
      onClick: () => void;
    },
    description?: string
  ) => (
    <div>
      <button className="button-link" onClick={title.onClick}>
        <p className="user-name-cell__name">{title.label}</p>
      </button>
      {description ? <p className="small-grey-text">{description}</p> : null}
    </div>
  );

  const getDepartmentCell = (row: TEAMscanActivityTableRow) => {
    const { id: departmentId } = row;
    const department = departments[departmentId] ?? {};

    const { teamsInAssessmentInstance = [] } = row;
    const teamInvitedCount = teamsInAssessmentInstance.length;

    const entityDescription = `${teamInvitedCount} team${getS(
      teamInvitedCount ?? 0
    )} invited`;

    const { name = "" } = department;

    return {
      sortValue: name,
      displayValue: getEntityDisplayValue(
        {
          label: name,
          onClick: () => {
            onSetDepartmentBreakdown(row);
          },
        },
        entityDescription
      ),
    };
  };

  const getTeamCell = (row: TEAMscanActivityTableRow) => {
    const { id: teamId } = row;
    const team = allTeamsById[teamId] ?? {};
    const userAccountIds = team.teamMemberIds ?? [];
    const memberCount = userAccountIds?.length ?? 0;
    const entityDescription = `${memberCount} member${getS(memberCount)}`;
    const { teamName = "" } = team;
    return {
      sortValue: teamName,
      displayValue: getEntityDisplayValue(
        {
          label: teamName,
          onClick: () => {
            onSeeTeamScanResultsForRow(row);
          },
        },
        entityDescription
      ),
    };
  };

  const getRowDropdown = (row: TEAMscanActivityTableRow) => ({
    displayValue: (
      <Dropdown
        onSelect={(e) => {
          onRowDropdownSelect(e, row);
        }}
      >
        <Dropdown.Toggle
          variant="outline-primary"
          id="dropdown-basic"
          className="no-caret"
        >
          <FontAwesomeIcon icon="ellipsis" />
        </Dropdown.Toggle>
        <TableDropdownMenu>
          <Dropdown.Item eventKey="teamScan">View TEAMscan</Dropdown.Item>
          {entitySelected === "department" ? (
            <>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="breakdown">View Breakdown</Dropdown.Item>
            </>
          ) : null}
        </TableDropdownMenu>
      </Dropdown>
    ),
    sortValue: "",
  });

  const getTableRow = (row: TEAMscanActivityTableRow) => {
    const {
      id: entityId,
      peopleOnAverageOrBelowTeams,
      peopleNotLikelyToStayOnTeam,
      overallScore,
      assessmentEventsCount,
      completedAssessmentEventsCount,
      peopleWithCompletedEvents,
      startDate,
      endDate,
      factorScores,
      dimensionScores,
    } = row;

    const hasResults = overallScore !== null && overallScore !== undefined;

    // The API names each row's company; the lookups below only know the current company's entities.
    const rowCompanyAccountId =
      row.companyAccountId ??
      (entitySelected === "team"
        ? allTeamsById[entityId]?.companyAccountId
        : departments[entityId]?.companyAccountId);

    const rowData: RowContent = {
      [entitySelected]:
        entitySelected === "team" ? getTeamCell(row) : getDepartmentCell(row),
      ...(companySelector
        ? {
            company: getCompanyName(rowCompanyAccountId, companyInfo, childCompanies),
          }
        : {}),
      startDate: new Date(startDate).toLocaleDateString("en-US"),
      completionRate: getScoreCell(
        completedAssessmentEventsCount,
        assessmentEventsCount,
        { showFraction: true, hasResults: true }
      ),
      overallScore: getOverallScore(overallScore, endDate),
    };

    if (showAllDimensions) {
      ALL_TEAM_360_FACTORS.forEach((factor) => {
        rowData[`factor_${factor}`] = getFactorOrDimensionScoreCell(
          factorScores?.[factor],
          endDate
        );
      });

      Object.keys(CONSTRUCT_NAME_KEY).forEach((dimensionName) => {
        rowData[`dimension_${dimensionName}`] = getFactorOrDimensionScoreCell(
          dimensionScores?.[dimensionName],
          endDate
        );
      });
    }

    rowData.peopleOnAverageAndBelow = getScoreCell(
      peopleOnAverageOrBelowTeams ?? 0,
      peopleWithCompletedEvents,
      { inverseColor: true, hasResults }
    );
    rowData.peopleNotLikelyToStayOnTeam = getScoreCell(
      peopleNotLikelyToStayOnTeam ?? 0,
      peopleWithCompletedEvents,
      { inverseColor: true, hasResults }
    );
    rowData.dropdown = getRowDropdown(row);

    return rowData;
  };

  const getTitleSection = () => {
    if (!breakdownRowSelected) {
      return (
        <div>
          <div className="flex justify-between align-items-center">
            <div className="column-gap-8px">
              <h2>TEAMscan Activity</h2>
              <p>Showing activity in this entire organization</p>
            </div>
            <div className="row-gap-8px align-items-center">
              {companySelector}
              <Button
                onClick={() => {
                  navigate("/OrganizationGuide?tab=TEAMscan");
                }}
                className="h-fit"
                variant="secondary-blue"
                disabled={activityTableData.length === 0}
              >
                Organization TEAMscan Report
              </Button>
            </div>
          </div>
          <div className="warning-banner lighter-blue flex mt-2">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1" />
            <div>
              <p className="fw-bold">
                Scores shown are the aggregate TEAMscan result for each team.
              </p>
              <p>
                Export CSV includes all dimensions and individual member data.
              </p>
            </div>
          </div>
        </div>
      );
    }

    const dataRangeString = "Aug 6 - Aug 12, 2021";

    const entitySelectedText = getEntitySelectedText();
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="column-gap-8px">
          <h2>Completion Activity Breakdown</h2>
          <div className="row-gap-4px grey-text">
            <p>
              Check-In activity for {entitySelectedText} from {dataRangeString}
            </p>
          </div>
        </div>
        <ExportCSVButton
          headers={getCSVHeaders()}
          getRows={getCSVExportRows}
          fileName="TEAMscan Activity"
          disabled={isTableLoading}
        />
      </div>
    );
  };

  const getEntitySelectedText = () => {
    if (!breakdownRowSelected) {
      return "";
    }

    if (breakdownRowSelected.type === "department") {
      const departmentName =
        departments[breakdownRowSelected.departmentId]?.name ?? "";
      return departmentName.toLowerCase().includes("department")
        ? departmentName
        : `${departmentName} Department`;
    }
    return "";
  };

  const getHeaders = () => {
    const nameHeaderLabel =
      entitySelected.charAt(0).toUpperCase() + entitySelected.slice(1);
    const mainHeaders: TableHeader[] = [
      { label: nameHeaderLabel, key: entitySelected },
      ...(companySelector
        ? [{ label: "Company", key: "company" }]
        : []),
      { label: "Start Date", key: "startDate", isDate: true },
      { label: "Completion Rate", key: "completionRate", sortInverse: true },
      { label: "Teamwork Score", key: "overallScore", sortInverse: true },
    ];

    if (showAllDimensions) {
      ALL_TEAM_360_FACTORS.forEach((factor) => {
        mainHeaders.push({
          label: factor,
          key: `factor_${factor}`,
          sortInverse: true,
        });
      });

      Object.entries(CONSTRUCT_NAME_KEY).forEach(([dimensionName]) => {
        mainHeaders.push({
          label: dimensionName,
          key: `dimension_${dimensionName}`,
          sortInverse: true,
        });
      });
    }

    mainHeaders.push(
      {
        label: "# People on Avg. And Below Teams",
        key: "peopleOnAverageAndBelow",
      },
      {
        label: "# People Not Likely To Stay On Team",
        key: "peopleNotLikelyToStayOnTeam",
      },
      {
        key: "dropdown",
        label: "",
        className: "table-dropdown-header",
      }
    );

    return mainHeaders;
  };

  const getCSVHeaders = () => {
    const headerLabels: { label: string; key: string }[] = [
      {
        label: entitySelected.charAt(0).toUpperCase() + entitySelected.slice(1),
        key: entitySelected,
      },
      ...TEAMSCAN_ACTIVITY_CSV_TABLE_HEADERS,
    ];

    ALL_TEAM_360_FACTORS.forEach((factor) => {
      headerLabels.push({
        label: factor,
        key: factor,
      });
    });

    Object.entries(CONSTRUCT_NAME_KEY).forEach(([key, label]) => {
      headerLabels.push({
        label: label,
        key: key,
      });
    });

    return headerLabels;
  };

  const rows = getFilteredRows().map(getTableRow);
  const headers = getHeaders();

  return (
    <>
      {breakdownRowSelected && (
        <div style={{ marginBottom: "20px" }}>
          <NavigateBackButton onClick={onBackNavigate} />
        </div>
      )}

      <Card>
        {getTitleSection()}

        <TableSearchInput
          value={displayedInput}
          onChange={setDisplayedInput}
          placeholder={getSearchPlaceholder()}
          className="min-w-[300px]"
        />
        {getDropdownSection()}

        <SortableTable
          rows={rows}
          tableClassName="admin-console-table"
          columnHeaders={[...headers]}
          isLoading={isTableLoading}
          isFirstColumnSticky={showAllDimensions}
        />
        <TableEmptyState
          isLoading={isTableLoading}
          totalDataCount={activityTableData.length}
          filteredCount={rows.length}
          noDataTitle="No TEAMscan activity found"
          noDataBody="Once there is TEAMscan activity, it will be shown here."
          entitySelected={entitySelected}
          searchInput={displayedInput}
        />
      </Card>
    </>
  );
};

export default TEAMscanActivityTable;
